import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14, default as _styled15 } from "styled-components";
import React from "react";
import type { SanityMediaValue, SanityPageBuilderMediaItem, SanityRichTextValue } from "@ouster/shared";
import { Icon } from "~/features/ui/icon";
import { RichText } from "~/features/ui/rich-text";
import { type Shape, Slider } from "~/features/ui/slider";
import { Progress } from "~/features/ui/slider/progress";
import CaretLeft from "~/public/icons/caret-left.svg";
import CaretRight from "~/public/icons/caret-right.svg";
import { hasRenderableSanityMedia, SanityMediaFigure, toSupportedRatio } from "~/features/sanity/page-builder/sections/media-figure";
import { toCraftRichText } from "./to-html";
type RichTextMediaBlock = {
  _key?: string;
  _type?: string;
  media?: SanityMediaValue | null;
};
type RichTextMediaItemsBlock = {
  _key?: string;
  _type?: string;
  items?: SanityPageBuilderMediaItem[];
};
type RichTextQuoteBlock = {
  _key?: string;
  _type?: string;
  author?: {
    description?: string;
    name?: string;
  };
  quoteText?: SanityRichTextValue;
};
export function SanityRichTextMediaBlock({
  value
}: {
  value: RichTextMediaBlock;
}) {
  if (!hasRenderableSanityMedia(value.media)) {
    return null;
  }
  return <_StyledFigure data-richtext={value._type} $_css={[{
    "marginTop": "4rem",
    "marginBottom": "4rem",
    "display": "flex",
    "flexDirection": "column",
    "gap": "1.6rem"
  }]}>
      <SanityMediaFigure media={value.media} ratio={toSupportedRatio(value.media.aspectRatio)} />
    </_StyledFigure>;
}
export function SanityRichTextMediaGridBlock({
  value
}: {
  value: RichTextMediaItemsBlock;
}) {
  const items = value.items?.filter(item => hasRenderableSanityMedia(item.media)) ?? [];
  if (!items.length) {
    return null;
  }
  return <_StyledUl data-richtext={value._type} $_css2={[{
    "marginTop": "4rem",
    "marginBottom": "4rem",
    "display": "grid",
    "gridTemplateColumns": "repeat(1, minmax(0, 1fr))",
    "gap": "1.6rem",
    "@media (min-width: 1024px)": {
      "gridTemplateColumns": "repeat(2, minmax(0, 1fr))"
    }
  }]}>
      {items.map((item, index) => <li key={item._key || index}>
          <figure>
            <SanityMediaFigure fit="cover" media={item.media} />
          </figure>
        </li>)}
    </_StyledUl>;
}
export function SanityRichTextMediaSliderBlock({
  value
}: {
  value: RichTextMediaItemsBlock;
}) {
  const items = value.items?.filter(item => hasRenderableSanityMedia(item.media)) ?? [];
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);
  const [lastSlideIndex, setLastSlideIndex] = React.useState(0);
  const sliderRef = React.useRef<Shape>(null);
  if (!items.length) {
    return null;
  }
  return <_StyledDiv data-richtext={value._type} $_css3={[{
    "marginTop": "4rem",
    "marginBottom": "4rem"
  }]}>
      <Slider ref={sliderRef} onMounted={splide => setLastSlideIndex(splide.Components.Controller.getEnd())} onMove={splide => {
      setActiveSlideIndex(splide.Components.Controller.getIndex());
      setLastSlideIndex(splide.Components.Controller.getEnd());
    }} onResize={splide => setLastSlideIndex(splide.Components.Controller.getEnd())} options={{
      fixedWidth: "88%",
      gap: 16,
      mediaQuery: "min",
      breakpoints: {
        1024: {
          fixedWidth: "42%"
        }
      }
    }}>
        {items.map((item, index) => <Slider.Slide key={item._key || index}>
            <figure>
              <SanityMediaFigure media={item.media} ratio="5/6" sizes="lg:42vw" />
            </figure>
          </Slider.Slide>)}
      </Slider>

      {items.length > 1 && <_StyledDiv2>
          <_StyledDiv3>
            <Progress activeSlideIndex={activeSlideIndex} totalSlides={lastSlideIndex + 1} />
          </_StyledDiv3>
          <_StyledDiv4>
            <_StyledButton type="button" onClick={() => sliderRef.current?.go("<")} disabled={activeSlideIndex === 0} $_css4={[{
          "display": "flex",
          "height": "3.2rem",
          "width": "3.2rem",
          "alignItems": "center",
          "justifyContent": "center",
          "borderRadius": "0",
          "borderBottomLeftRadius": "4px",
          "borderTopLeftRadius": "4px",
          "backgroundColor": "var(--bg-transparent-20) !important",
          "color": "var(--text-primary)",
          "transitionProperty": "opacity"
        }]}>
              <_StyledIcon icon={CaretLeft} size="16" $_css5={[activeSlideIndex === 0 && {
            "opacity": "0.3"
          }]} />
            </_StyledButton>
            <_StyledButton2 type="button" onClick={() => sliderRef.current?.go(">")} disabled={activeSlideIndex >= lastSlideIndex} $_css6={[{
          "display": "flex",
          "height": "3.2rem",
          "width": "3.2rem",
          "alignItems": "center",
          "justifyContent": "center",
          "borderRadius": "0",
          "borderBottomRightRadius": "4px",
          "borderTopRightRadius": "4px",
          "backgroundColor": "var(--bg-transparent-20) !important",
          "color": "var(--text-primary)",
          "transitionProperty": "opacity"
        }]}>
              <_StyledIcon2 icon={CaretRight} size="16" $_css7={[activeSlideIndex >= lastSlideIndex && {
            "opacity": "0.3"
          }]} />
            </_StyledButton2>
          </_StyledDiv4>
        </_StyledDiv2>}
    </_StyledDiv>;
}
export function SanityRichTextQuoteBlock({
  value
}: {
  value: RichTextQuoteBlock;
}) {
  const quoteText = toCraftRichText(value.quoteText);
  if (!quoteText) {
    return null;
  }
  return <_StyledFigure2 data-richtext={value._type} $_css8={[{
    "marginTop": "4rem",
    "marginBottom": "4rem",
    "borderLeftWidth": "4px",
    "--tw-border-opacity": "1",
    "borderColor": "rgb(75 46 248 / var(--tw-border-opacity))",
    "paddingLeft": "2.4rem"
  }]}>
      <_StyledBlockquote>
        <RichText text={quoteText} />
      </_StyledBlockquote>
      {(value.author?.name || value.author?.description) && <_StyledFigcaption>
          {value.author?.name && <_StyledSpan>{value.author.name}</_StyledSpan>}
          {value.author?.description && <_StyledSpan2>{value.author.description}</_StyledSpan2>}
        </_StyledFigcaption>}
    </_StyledFigure2>;
}
var _StyledFigure = /*#__PURE__*/_styled("figure").withConfig({
  componentId: "sc-sf-1ezmjdt-0"
})`${p => p.$_css}`;
var _StyledUl = _styled2("ul").withConfig({
  componentId: "sc-sf-1ezmjdt-1"
})`${p => p.$_css2}`;
var _StyledDiv = _styled3("div").withConfig({
  componentId: "sc-sf-1ezmjdt-2"
})`${p => p.$_css3}`;
var _StyledDiv2 = _styled4("div").withConfig({
  componentId: "sc-sf-1ezmjdt-3"
})({
  "marginTop": "2.4rem",
  "display": "flex",
  "alignItems": "center",
  "gap": "var(--spacing-lg)"
});
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-1ezmjdt-4"
})({
  "flex": "1 1 0%"
});
var _StyledDiv4 = _styled6("div").withConfig({
  componentId: "sc-sf-1ezmjdt-5"
})({
  "display": "flex"
});
var _StyledButton = _styled7("button").withConfig({
  componentId: "sc-sf-1ezmjdt-6"
})`${p => p.$_css4}`;
var _StyledIcon = _styled8(Icon).withConfig({
  componentId: "sc-sf-1ezmjdt-7"
})`${p => p.$_css5}`;
var _StyledButton2 = _styled9("button").withConfig({
  componentId: "sc-sf-1ezmjdt-8"
})`${p => p.$_css6}`;
var _StyledIcon2 = _styled10(Icon).withConfig({
  componentId: "sc-sf-1ezmjdt-9"
})`${p => p.$_css7}`;
var _StyledFigure2 = _styled11("figure").withConfig({
  componentId: "sc-sf-1ezmjdt-10"
})`${p => p.$_css8}`;
var _StyledBlockquote = _styled12("blockquote").withConfig({
  componentId: "sc-sf-1ezmjdt-11"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledFigcaption = _styled13("figcaption").withConfig({
  componentId: "sc-sf-1ezmjdt-12"
})({
  "marginTop": "1.6rem",
  "display": "flex",
  "flexWrap": "wrap",
  "gap": "0.8rem",
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400"
});
var _StyledSpan = _styled14("span").withConfig({
  componentId: "sc-sf-1ezmjdt-13"
})({
  "fontWeight": "700",
  "--tw-text-opacity": "1",
  "color": "rgb(75 46 248 / var(--tw-text-opacity))"
});
var _StyledSpan2 = /*#__PURE__*/_styled15("span").withConfig({
  displayName: "blocks___StyledSpan2",
  componentId: "sc-1pz6doe-0"
})({
  "color": "var(--text-transparent)"
});